import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';
import { getTrustTermDisplay } from './utils';

interface CltIncomeCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  incomePayments?: EstateWaterfall_TransferLogItemFragment[];
  cltFunding?: EstateWaterfall_TransferLogItemFragment;
}

export function createCltIncomeCard({
  sourceEntityName,
  targetEntityName,
  incomePayments,
  cltFunding,
}: CltIncomeCardParams): CardConfig {
  const cltName = sourceEntityName;
  const incomeBeneficiary = targetEntityName;

  const isHypothetical = incomePayments?.[0]?.isHypothetical;

  const fundingAmount = (() => {
    if (cltFunding) {
      return cltFunding.finalTransferAmount;
    }

    return null;
  })();

  // Calculate total payments
  const totalPayments =
    incomePayments?.reduce((sum, payment) => {
      return sum.plus(payment.finalTransferAmount || 0);
    }, new Decimal(0)) || new Decimal(0);

  const term = (() => {
    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'HypotheticalCLT'
    ) {
      const termYears =
        incomePayments[0].associatedObject.simulatedClt.cltProposal?.termYears;
      return termYears ? `${termYears} years` : null;
    }

    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'Entity' &&
      incomePayments[0].associatedObject.subtype?.__typename === 'CLTTrust'
    ) {
      const { termDurationLifetime, termDurationYears } =
        incomePayments[0].associatedObject.subtype;
      return getTrustTermDisplay({ termDurationLifetime, termDurationYears });
    }

    return null;
  })();

  const termEndYear = (() => {
    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'HypotheticalCLT'
    ) {
      const termYears =
        incomePayments[0].associatedObject.simulatedClt.cltProposal?.termYears;
      return termYears ? new Date().getFullYear() + termYears : null;
    }

    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'Entity' &&
      incomePayments[0].associatedObject.subtype?.__typename === 'CLTTrust'
    ) {
      const { termDurationLifetime } =
        incomePayments[0].associatedObject.subtype;
      if (termDurationLifetime) {
        return null;
      }
      return incomePayments[0].associatedObject.subtype.termEndDate?.getFullYear();
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`clt-income-${sourceEntityName}-${targetEntityName}`}
        title={
          isHypothetical
            ? 'Hypothetical CLT income payments'
            : 'CLT income payments'
        }
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${incomeBeneficiary} receives income payments from ${cltName} throughout CLT term.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          {
            label: 'Total income payments',
            value: formatCurrencyNoDecimals(totalPayments),
          },
          term
            ? {
                label: 'CLT term',
                value: term,
              }
            : null,
          termEndYear
            ? {
                label: 'Term end',
                value: termEndYear.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'CLT income',
    value: formatCurrencyNoDecimals(totalPayments),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
